/**
 * safeArea.js
 *
 * Shared safe-area helpers so every header, screen wrapper and floating banner
 * sits clear of the iPhone notch / Dynamic Island and the Android status bar —
 * instead of the old hardcoded `paddingTop: 52` / `getStatusBarHeight()` values
 * that collided with the Dynamic Island (≈59pt) on iPhone 14 Pro and newer.
 *
 * These hooks require a <SafeAreaProvider> above them in the tree (mounted once
 * at the app root in AppProviders.jsx). They degrade gracefully:
 *  - web: no hardware safe area → 0.
 *  - Android: the system status bar height (still reported even when the bar is
 *    hidden) so headers never hug the very top edge.
 *  - iOS: the real `UIView.safeAreaInsets`, which already accounts for the
 *    notch / Dynamic Island and persists even when the status bar is hidden;
 *    falls back to 20 on the rare device that reports 0.
 *
 * ---------------------------------------------------------------------------
 * WHY THESE VALUES ARE LATCHED (regression fix — "the ticking bug")
 * ---------------------------------------------------------------------------
 * The values returned here are written straight into layout: the main bottom
 * tab bar is sized `height: 62 + useBottomInset()` with a matching
 * `paddingBottom` (HomeScreen/OnlineScreen/TablesScreen/WarehousemanHomeScreen),
 * and the floating module bars are positioned `bottom: 10 + useBottomInset()`
 * (ManagementHomeScreen/RepairmanHomeScreen/ModuleTabBar).
 *
 * That makes an inset a *layout input*, so any instability in the value is
 * immediately visible as the whole scene jumping up and down. On Android the
 * native side re-dispatches window insets very freely — `onConfigurationChanged`
 * (our MainActivity declares `screenSize|screenLayout|uiMode`, so the activity
 * is never recreated and every change arrives as a live callback), IME
 * pan/resize, multi-window, and repeated `onApplyWindowInsets` passes on some
 * OEM ROMs — and each dispatch can briefly report a different value.
 *
 * So both hooks pass their result through {@link useSettledValue}: a new number
 * is only published once it has held steady for {@link SETTLE_MS}. A value that
 * flips back and forth never settles, so it never reaches layout and the UI
 * simply holds still; a genuine change (rotation, entering split-screen) settles
 * within a few hundred milliseconds and is applied once.
 *
 * The previous implementation additionally took
 * `Math.max(insets.bottom, Dimensions.get('screen').height - Dimensions.get('window').height)`.
 * That `screen - window` term was removed — see {@link useBottomInset} for why
 * it was measuring the wrong quantity.
 *
 * @module helpers/safeArea
 */

import {useEffect, useRef, useState} from 'react';
import {Platform, StatusBar} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

/**
 * How long a new inset reading must hold steady before it is allowed to reach
 * layout. Long enough to swallow a burst of native inset dispatches, short
 * enough that a real rotation settles before the user notices.
 * @type {number}
 */
export const SETTLE_MS = 300;

/**
 * Differences at or below this are density-rounding churn, not a real change.
 * @type {number}
 */
export const EPSILON_PX = 2;

/**
 * The single decision {@link useSettledValue} makes on each render: is this
 * reading different enough from the currently published one to be worth
 * scheduling a publish for?
 *
 * Extracted as a pure function so the latch's oscillation behaviour can be
 * unit-tested without a React renderer (see `app/tests/safeAreaSettle.test.js`).
 *
 * @param {number} value      Latest raw reading.
 * @param {number} published  Value currently in effect.
 * @param {number} [epsilon=EPSILON_PX]  Rounding-noise tolerance.
 * @returns {boolean} true when a publish should be scheduled for `value`.
 */
export const shouldScheduleSettle = (value, published, epsilon = EPSILON_PX) =>
    Math.abs(value - published) > epsilon;

/**
 * Publishes `value` only once it has held steady for `settleMs`.
 *
 * Every time `value` changes the pending timer is cancelled and restarted, so a
 * value that oscillates between two readings never reaches the returned result
 * and never triggers a state update — which is precisely what stops an unstable
 * native inset from turning into a visibly ticking layout. A value that changes
 * once and then stays put is published after a single `settleMs` delay.
 *
 * @param {number} value    Raw, possibly unstable measurement.
 * @param {number} [settleMs=SETTLE_MS]  Quiet period the value must survive.
 * @returns {number} The most recent value that held steady for `settleMs`.
 */
export const useSettledValue = (value, settleMs = SETTLE_MS) => {
    const [settled, setSettled] = useState(value);
    const settledRef = useRef(value);

    useEffect(() => {
        // Already the published value (or within rounding noise of it) — nothing
        // to schedule, and importantly no state update, so an A→B→A flip is a
        // complete no-op for layout.
        if (!shouldScheduleSettle(value, settledRef.current)) {
            return undefined;
        }
        const timer = globalThis.setTimeout(() => {
            settledRef.current = value;
            setSettled(value);
        }, settleMs);
        return () => globalThis.clearTimeout(timer);
    }, [value, settleMs]);

    return settled;
};

/**
 * Top inset to clear the status bar / notch / Dynamic Island.
 * @returns {number} pixels of safe padding to apply at the top of a screen/header.
 */
export const useTopInset = () => {
    const insets = useSafeAreaInsets();

    let raw = 0;
    if (Platform.OS === 'android') {
        raw = Math.round(insets?.top || StatusBar.currentHeight || 0);
    } else if (Platform.OS !== 'web') {
        // iOS — Dynamic Island / notch report ~47-59; old devices ~20.
        raw = Math.round(insets?.top || 20);
    }

    const settled = useSettledValue(raw);
    return Platform.OS === 'web' ? 0 : settled;
};

/**
 * Bottom inset to clear the iOS home indicator / Android gesture bar / Android
 * three-button navigation bar.
 *
 * The single source of truth is `react-native-safe-area-context`, which reads
 * the real `WindowInsetsCompat` and is correct in both worlds:
 *  - drawing edge-to-edge → the nav bar overlaps our content and `insets.bottom`
 *    reports its height, which we must pad by ourselves;
 *  - not drawing edge-to-edge (this app: `expo.edgeToEdgeEnabled=false` in
 *    android/gradle.properties plus `android:windowOptOutEdgeToEdgeEnforcement`
 *    in styles.xml) → the system has already carved the nav bar out of our
 *    window, `insets.bottom` is 0, and 0 is the right answer.
 *
 * ### Why the old `screen - window` fallback is gone
 * It was introduced to cover OEM ROMs that report `insets.bottom === 0`, via
 * `Math.max(insets.bottom, Dimensions.get('screen').height - Dimensions.get('window').height)`.
 * That difference is not the navigation bar height. It is the *total* vertical
 * chrome the system has excluded from our window — status bar + nav bar +
 * display cutout — and it also goes non-zero for a panned/resized IME, for
 * split-screen and freeform multi-window, and for picture-in-picture.
 *
 * Worse, its sign is inverted with respect to what a bottom inset means: a large
 * `screen - window` says the system already reserved that space *outside* our
 * window, so the correct padding is zero — yet `Math.max` turned it into the
 * largest possible padding. With edge-to-edge off that is a pure double-count,
 * which is what left a dead strip between the tab bar and the physical nav bar.
 * And because it was read from `Dimensions` (a different native callback than
 * the one that drives `insets`), the two sources updated out of step and
 * `Math.max` amplified every transient disagreement into a full ~48-72px layout
 * change. Combined across the eight components that call this hook — each with
 * its own `useState` and its own `Dimensions.addEventListener('change')` — that
 * is the tab-bar/scene oscillation reported on Android.
 *
 * If a specific device ever genuinely needs padding that `WindowInsetsCompat`
 * does not report, the fix belongs in the native inset configuration (or an
 * explicit per-device constant), not in a `Dimensions` difference that measures
 * a different quantity.
 *
 * @returns {number} pixels of safe padding to apply at the bottom of a screen.
 */
export const useBottomInset = () => {
    const insets = useSafeAreaInsets();
    const raw = Math.max(0, Math.round(insets?.bottom || 0));
    const settled = useSettledValue(raw);
    return Platform.OS === 'web' ? 0 : settled;
};

export default {
    useTopInset,
    useBottomInset,
    useSettledValue,
    shouldScheduleSettle,
    SETTLE_MS,
    EPSILON_PX,
};
